import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

const FlashMessage = ({
  message,
  type = 'success', // 'success', 'danger', 'warning', 'info'
  duration = 5000,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onClose && onClose(), 300); // Wait for animation
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getTypeConfig = () => {
    switch (type) {
      case 'success':
        return {
          icon: CheckCircle,
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-800',
          iconColor: 'text-green-600'
        };
      case 'danger':
        return {
          icon: XCircle,
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-800',
          iconColor: 'text-red-600'
        };
      case 'warning':
        return {
          icon: AlertTriangle,
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          textColor: 'text-yellow-800',
          iconColor: 'text-yellow-600'
        };
      case 'info':
        return {
          icon: Info,
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-800',
          iconColor: 'text-blue-600'
        };
      default:
        return {
          icon: CheckCircle,
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-800',
          iconColor: 'text-green-600'
        };
    }
  };

  const config = getTypeConfig();
  const Icon = config.icon;

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose && onClose(), 300);
  };

  if (!message) return null;

  return (
    <div
      className={`fixed top-4 right-4 z-50 max-w-sm w-full transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
      }`}
    >
      <div className={`rounded-lg border p-4 shadow-lg ${config.bgColor} ${config.borderColor}`}>
        <div className="flex items-start space-x-3">
          <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${config.iconColor}`} />
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${config.textColor}`}>
              {message}
            </p>
          </div>
          <button
            onClick={handleClose}
            className={`flex-shrink-0 p-1 rounded-full hover:bg-black hover:bg-opacity-10 transition-colors ${config.textColor}`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Flash mesaj yöneticisi hook'u
export const useFlashMessage = () => {
  const [flashMessages, setFlashMessages] = useState([]);

  const showMessage = (message, type = 'success', duration = 5000) => {
    const id = Date.now();
    const newMessage = { id, message, type, duration };

    setFlashMessages(prev => [...prev, newMessage]);
  };

  const removeMessage = (id) => {
    setFlashMessages(prev => prev.filter(msg => msg.id !== id));
  };

  const FlashContainer = () => {
    if (flashMessages.length === 0) return null;

    return (
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {flashMessages.map(msg => (
          <FlashMessage
            key={msg.id}
            message={msg.message}
            type={msg.type}
            duration={msg.duration}
            onClose={() => removeMessage(msg.id)}
          />
        ))}
      </div>
    );
  };

  return {
    showSuccess: (message) => showMessage(message, 'success'),
    showDanger: (message) => showMessage(message, 'danger'),
    showWarning: (message) => showMessage(message, 'warning'),
    showInfo: (message) => showMessage(message, 'info'),
    FlashContainer
  };
};

export default FlashMessage;